import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { Button, cn, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@tellescope/ui";
import { Tooltip } from "@tellescope/design-system";
import { useVideoCalling } from "../hooks/useVideoCallingContext";
import { EffectIcon } from "./EffectIcon";
import type { BackgroundEffect } from "../types";

export interface BackgroundEffectSelectorProps {
  /** Visual style: "call" (dark in-call control bar) or "lobby" (pre-join preview). */
  variant?: "call" | "lobby";
  /** Disable the control (e.g. when there are no working tracks in the lobby). */
  disabled?: boolean;
}

/**
 * BackgroundEffectSelector - Shared None/Blur/Image background effect menu.
 *
 * Used by both the in-call control bar and the pre-join lobby. The "Image" option
 * only appears when a background image URL is available (provider webapp only);
 * otherwise the menu degrades to None/Blur, functionally identical to the prior blur toggle.
 */
export const BackgroundEffectSelector = ({ variant = "call", disabled = false }: BackgroundEffectSelectorProps) => {
  const { t } = useTranslation("videoCalling");
  const { isEffectSupported, isEffectLoading, backgroundEffect, backgroundImageURL, setBackgroundEffect } =
    useVideoCalling();

  if (!isEffectSupported) return null;

  const isImageBackgroundAvailable = isEffectSupported && !!backgroundImageURL;
  const isCall = variant === "call";
  const iconClass = cn(isCall ? "h-5 w-5" : "h-4 w-4");
  const isActive = backgroundEffect !== "none";

  const buttonClassName = isCall
    ? cn(
        "rounded-full h-12 w-12 !bg-gray-800 !border-gray-600 !text-white hover:!bg-gray-700",
        isActive && "!bg-emerald-500/20 !text-emerald-300 !border-emerald-400/50 hover:!bg-emerald-500/30"
      )
    : cn("rounded-full h-10 w-10", isActive && "bg-emerald-500/20 text-emerald-300 border-emerald-400/50");

  const options: { effect: BackgroundEffect; label: string }[] = [
    { effect: "none", label: t("None") },
    { effect: "blur", label: t("Blur") },
    ...(isImageBackgroundAvailable ? [{ effect: "image" as const, label: t("Image") }] : []),
  ];

  return (
    <DropdownMenu>
      <Tooltip>
        <Tooltip.Trigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              loading={isEffectLoading}
              disabled={disabled}
              className={buttonClassName}
            >
              <EffectIcon effect={backgroundEffect} className={iconClass} />
            </Button>
          </DropdownMenuTrigger>
        </Tooltip.Trigger>
        <Tooltip.Content>{t("Background effect")}</Tooltip.Content>
      </Tooltip>
      <DropdownMenuContent align="center">
        {options.map(({ effect, label }) => (
          <DropdownMenuItem key={effect} onSelect={() => setBackgroundEffect(effect)}>
            <EffectIcon effect={effect} className={cn("h-4 w-4 mr-2")} />
            <span className={cn("flex-1")}>{label}</span>
            {backgroundEffect === effect && <Check className={cn("h-4 w-4 ml-2")} />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
